import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { adminStoryService, AdminStoryDto, QueueStats } from '../../services/adminStoryService';
import { LoadingSpinner } from '../../shared/components';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { RejectReasonModal } from '../../components/RejectReasonModal';
import { RequestRevisionModal } from '../../components/RequestRevisionModal';
import { UrgentReasonModal } from '../../components/UrgentReasonModal';
import { SuccessAnimation } from '../../components/SuccessAnimation';
import { Select, Tooltip } from '../../shared/components/UI';
import toast from 'react-hot-toast';

// Helper function to format violation types for display
const formatViolations = (violations: string[]): string => {
  if (!violations || violations.length === 0) return '';

  const violationMap: Record<string, string> = {
    'VIOLENCE': 'Violence',
    'ADULT_THEMES': 'Adult Themes',
    'INAPPROPRIATE_LANGUAGE': 'Inappropriate Language',
    'BULLYING': 'Bullying/Discrimination',
    'SCARY_CONTENT': 'Scary Content',
    'DISCRIMINATION': 'Discrimination',
    'AGE_INAPPROPRIATE': 'Age-Inappropriate'
  };

  return violations
    .map(v => violationMap[v] || v.replace(/_/g, ' '))
    .join(', ');
};

// Helper function to calculate severity from confidence and violations
const calculateSeverity = (confidence: number, violationCount: number): 'HIGH' | 'MEDIUM' | 'LOW' => {
  if (confidence >= 0.9 || violationCount >= 3) return 'HIGH';
  if (confidence >= 0.7 || violationCount >= 2) return 'MEDIUM';
  return 'LOW';
};

const StoryQueueContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const QueueStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const StatCard = styled.div`
  background: var(--color-white);
  padding: 1.5rem;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-gray-800);
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: var(--color-gray-600);
  font-weight: 500;
`;

const FiltersCard = styled.div`
  background: var(--color-white);
  padding: 1.5rem;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
`;

const FilterSelect = styled.select`
  padding: 0.5rem 0.75rem;
  border: 2px solid var(--color-gray-300);
  border-radius: var(--border-radius-md);
  font-size: 0.875rem;
  background: var(--color-white);
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const StoryQueue = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const StoryCard = styled.div<{ $status: string }>`
  background: var(--color-white);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  border-left: 4px solid ${props => {
    switch (props.$status) {
      case 'AI_REVIEW_PENDING':
      case 'PENDING_REVIEW': return '#3B82F6';
      case 'ADMIN_REVIEW_PENDING': return '#F59E0B';
      case 'REJECTED': return '#EF4444';
      case 'REVISION_REQUESTED': return '#FB923C'; // Orange for revisions
      case 'APPEAL_PENDING': return '#8B5CF6';
      default: return '#10B981';
    }
  }};
  overflow: hidden;
`;

const StoryHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--color-gray-200);
`;

const StoryTitle = styled.h3`
  margin: 0 0 0.75rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-gray-900);
`;

const StoryMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  font-size: 0.875rem;
  color: var(--color-gray-600);
  margin-bottom: 1rem;
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const StatusBadge = styled.span<{ $status: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: var(--border-radius-full);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  ${props => {
    switch (props.$status) {
      case 'AI_REVIEW_PENDING':
      case 'PENDING_REVIEW':
        return `background: #DBEAFE; color: #1E40AF;`;
      case 'ADMIN_REVIEW_PENDING':
        return `background: #FEF3C7; color: #92400E;`;
      case 'REJECTED':
        return `background: #FEE2E2; color: #991B1B;`;
      case 'REVISION_REQUESTED':
        return `background: #FED7AA; color: #9A3412;`;
      case 'APPEAL_PENDING':
        return `background: #EDE9FE; color: #6B21A8;`;
      default:
        return `background: #D1FAE5; color: #065F46;`;
    }
  }}
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ $variant: 'approve' | 'reject' | 'revision' | 'view' | 'priority' }>`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => {
    switch (props.$variant) {
      case 'approve':
        return `
          background: #10B981;
          color: white;
          &:hover { background: #059669; }
        `;
      case 'reject':
        return `
          background: #EF4444;
          color: white;
          &:hover { background: #DC2626; }
        `;
      case 'revision':
        return `
          background: #F59E0B;
          color: white;
          &:hover { background: #D97706; }
        `;
      case 'view':
        return `
          background: var(--color-primary);
          color: white;
          &:hover { background: var(--color-primary-600); }
        `;
      case 'priority':
        return `
          background: #8B5CF6;
          color: white;
          &:hover { background: #7C3AED; }
        `;
    }
  }}
`;

const StoryContent = styled.div`
  padding: 1.5rem;
  background: var(--color-gray-50);
`;

const AIAnalysisCard = styled.div`
  background: var(--color-white);
  border: 2px solid var(--color-blue-200);
  border-radius: var(--border-radius-md);
  padding: 1rem;
  margin-bottom: 1rem;
`;

const AIHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: between;
  margin-bottom: 0.75rem;
`;

const AITitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--color-blue-700);
`;

const ConfidenceScore = styled.span<{ $score: number }>`
  padding: 0.25rem 0.75rem;
  border-radius: var(--border-radius-full);
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: auto;
  
  ${props => {
    if (props.$score >= 0.8) {
      return `background: #FEE2E2; color: #991B1B;`;
    } else if (props.$score >= 0.5) {
      return `background: #FEF3C7; color: #92400E;`;
    } else {
      return `background: #D1FAE5; color: #065F46;`;
    }
  }}
`;

const ViolationsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

const ViolationTag = styled.span`
  padding: 0.25rem 0.5rem;
  background: #FEE2E2;
  color: #991B1B;
  border-radius: var(--border-radius-sm);
  font-size: 0.75rem;
  font-weight: 500;
`;

const SeverityBadge = styled.span<{ $severity: 'HIGH' | 'MEDIUM' | 'LOW' }>`
  padding: 0.25rem 0.75rem;
  border-radius: var(--border-radius-full);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-left: auto;

  ${props => {
    switch (props.$severity) {
      case 'HIGH':
        return `background: #FEE2E2; color: #991B1B;`;
      case 'MEDIUM':
        return `background: #FEF3C7; color: #92400E;`;
      case 'LOW':
        return `background: #DBEAFE; color: #1E40AF;`;
    }
  }}
`;

const AIMetadata = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--color-gray-200);
  font-size: 0.75rem;
  color: var(--color-gray-600);
`;

const StoryPreview = styled.div`
  background: var(--color-white);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--border-radius-md);
  padding: 1rem;
  max-height: 200px;
  overflow-y: auto;
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--color-gray-700);
`;

const UrgentBanner = styled.div`
  background: linear-gradient(135deg, #FEE2E2, #FECACA);
  border: 1px solid #F87171;
  border-radius: var(--border-radius-md);
  padding: 1rem;
  margin-bottom: 2rem;
  text-align: center;
  font-weight: 600;
  color: #991B1B;
`;

interface StorySubmission {
  id: string;
  title: string;
  author: string;
  genre: string;
  submittedAt: string;
  status: 'AI_REVIEW_PENDING' | 'ADMIN_REVIEW_PENDING' | 'REJECTED' | 'REVISION_REQUESTED' | 'APPEAL_PENDING' | 'PENDING_REVIEW';
  urgency: 'high' | 'medium' | 'low';
  aiAnalysis?: {
    confidence: number;
    violations: string[];
    explanation: string;
    appropriate: boolean;
    modelVersion?: string;
    analyzedAt?: string;
  };
  content: string;
  summary: string;
}

const StoryApprovalQueue: React.FC = () => {
  const [stories, setStories] = useState<StorySubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [queueStats, setQueueStats] = useState<QueueStats | null>(null);
  const [processingAction, setProcessingAction] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState('all');
  const [urgencyFilter, setUrgencyFilter] = useState('all');
  
  // Modal states
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    action: () => void;
    variant?: 'danger' | 'primary';
    icon?: string;
  }>({ isOpen: false, title: '', message: '', action: () => {} });
  
  const [rejectModal, setRejectModal] = useState<{
    isOpen: boolean;
    storyId: string;
    storyTitle: string;
  }>({ isOpen: false, storyId: '', storyTitle: '' });

  const [revisionModal, setRevisionModal] = useState<{
    isOpen: boolean;
    storyId: string;
    storyTitle: string;
  }>({ isOpen: false, storyId: '', storyTitle: '' });

  const [urgentModal, setUrgentModal] = useState<{
    isOpen: boolean;
    storyId: string;
    storyTitle: string;
  }>({ isOpen: false, storyId: '', storyTitle: '' });

  const [successAnimation, setSuccessAnimation] = useState<{
    isVisible: boolean;
    variant: 'approve' | 'reject' | 'urgent';
    message?: string;
  }>({ isVisible: false, variant: 'approve' });
  
  useEffect(() => {
    loadStories();
    loadQueueStats();
  }, [statusFilter]);
  
  const loadStories = async () => {
    try {
      setLoading(true);
      const response = await adminStoryService.getPendingStories({
        status: statusFilter === 'all' ? undefined : statusFilter,
        size: 50
      });
      
      const transformedStories = (response.content || []).map((story: AdminStoryDto) => ({
        id: story.id,
        title: story.title,
        author: story.authorId, // TODO: Fetch actual username
        genre: story.genre,
        submittedAt: story.submittedAt,
        status: story.status,
        urgency: story.urgency,
        aiAnalysis: story.aiAnalysis ? {
          confidence: story.aiAnalysis.confidence,
          violations: story.aiAnalysis.violations,
          explanation: story.aiAnalysis.explanation,
          appropriate: story.aiAnalysis.appropriate,
          modelVersion: story.aiAnalysis.modelVersion,
          analyzedAt: story.aiAnalysis.analyzedAt
        } : undefined,
        content: story.content || 'No content preview available',
        summary: story.summary || 'No summary available'
      }));
      
      setStories(transformedStories);
    } catch (error) {
      console.error('Failed to load stories:', error);
      toast.error('Failed to load stories');
    } finally {
      setLoading(false);
    }
  };
  
  const loadQueueStats = async () => {
    try {
      const stats = await adminStoryService.getQueueStats();
      setQueueStats(stats);
    } catch (error) {
      console.error('Failed to load queue stats:', error);
    }
  };

  const filteredStories = stories.filter(story => {
    return (
      (statusFilter === 'all' || story.status === statusFilter) &&
      (urgencyFilter === 'all' || story.urgency === urgencyFilter)
    );
  });

  const stats = queueStats || {
    totalPending: 0,
    aiReviewPending: 0,
    adminReviewPending: 0,
    appealPending: 0,
    urgent: 0
  };

  // Show approve confirmation dialog
  const handleApproveClick = (storyId: string, storyTitle: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Approve Story',
      message: `Are you sure you want to approve "${storyTitle}"? This will make the story available for public viewing.`,
      variant: 'primary',
      icon: '✅',
      action: () => handleApproveStory(storyId)
    });
  };

  // Show reject reason modal
  const handleRejectClick = (storyId: string, storyTitle: string) => {
    setRejectModal({
      isOpen: true,
      storyId,
      storyTitle
    });
  };

  // Show request revision modal
  const handleRequestRevisionClick = (storyId: string, storyTitle: string) => {
    setRevisionModal({
      isOpen: true,
      storyId,
      storyTitle
    });
  };

  // Show urgent reason modal
  const handleMarkUrgentClick = (storyId: string, storyTitle: string) => {
    setUrgentModal({
      isOpen: true,
      storyId,
      storyTitle
    });
  };

  // Actual approve action
  const handleApproveStory = async (storyId: string) => {
    try {
      setProcessingAction(storyId);
      setConfirmDialog({ ...confirmDialog, isOpen: false });
      
      await adminStoryService.approveStory(storyId, 'Story approved by admin');
      
      // Show success animation
      setSuccessAnimation({ 
        isVisible: true, 
        variant: 'approve',
        message: 'Story Approved Successfully!' 
      });
      
      // Show toast after animation
      setTimeout(() => {
        toast.success('✅ Story approved successfully!');
      }, 1000);
      
      // Reload stories and stats
      await Promise.all([loadStories(), loadQueueStats()]);
      
    } catch (error) {
      console.error('Failed to approve story:', error);
      toast.error('Failed to approve story');
    } finally {
      setProcessingAction(null);
    }
  };

  // Actual reject action with reason
  const handleRejectStory = async (reason: string) => {
    try {
      setProcessingAction(rejectModal.storyId);
      setRejectModal({ ...rejectModal, isOpen: false });

      await adminStoryService.rejectStory(rejectModal.storyId, reason);

      // Show success animation
      setSuccessAnimation({
        isVisible: true,
        variant: 'reject',
        message: 'Story Rejected Successfully!'
      });

      // Show toast after animation
      setTimeout(() => {
        toast.success('❌ Story rejected successfully!');
      }, 1000);

      // Reload stories and stats
      await Promise.all([loadStories(), loadQueueStats()]);

    } catch (error) {
      console.error('Failed to reject story:', error);
      toast.error('Failed to reject story');
    } finally {
      setProcessingAction(null);
    }
  };

  // Actual request revision action with notes
  const handleRequestRevision = async (revisionNotes: string) => {
    try {
      setProcessingAction(revisionModal.storyId);
      setRevisionModal({ ...revisionModal, isOpen: false });

      await adminStoryService.requestRevision(revisionModal.storyId, revisionNotes);

      // Show success toast
      toast.success('✏️ Revision request sent successfully! Author has been notified.');

      // Reload stories and stats
      await Promise.all([loadStories(), loadQueueStats()]);

    } catch (error) {
      console.error('Failed to request revision:', error);
      toast.error('Failed to request revision');
    } finally {
      setProcessingAction(null);
    }
  };

  // Actual mark urgent action with reason
  const handleMarkUrgent = async (reason: string) => {
    try {
      setProcessingAction(urgentModal.storyId);
      setUrgentModal({ ...urgentModal, isOpen: false });
      
      await adminStoryService.triggerAiReview(urgentModal.storyId); // Using existing endpoint for now
      
      // Show success animation
      setSuccessAnimation({ 
        isVisible: true, 
        variant: 'urgent',
        message: 'Marked as Urgent!' 
      });
      
      // Show toast after animation
      setTimeout(() => {
        toast.success('🚨 Story marked as urgent successfully!');
      }, 1000);
      
      // Reload stories and stats
      await Promise.all([loadStories(), loadQueueStats()]);
      
    } catch (error) {
      console.error('Failed to mark story as urgent:', error);
      toast.error('Failed to mark story as urgent');
    } finally {
      setProcessingAction(null);
    }
  };

  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      'PENDING_REVIEW': 'PENDING REVIEW',
      'AI_REVIEW_PENDING': 'AI REVIEW PENDING',
      'ADMIN_REVIEW_PENDING': 'ADMIN REVIEW PENDING',
      'APPEAL_PENDING': 'APPEAL PENDING',
      'REJECTED': 'REJECTED',
      'APPROVED': 'APPROVED'
    };
    return statusMap[status] || status;
  };

  const urgentStories = stories.filter(s => s.urgency === 'high');

  return (
    <StoryQueueContainer>
      {/* Urgent Banner */}
      {urgentStories.length > 0 && (
        <UrgentBanner>
          ⚠️ {urgentStories.length} urgent stor{urgentStories.length === 1 ? 'y' : 'ies'} require immediate attention!
        </UrgentBanner>
      )}

      {/* Stats */}
      <QueueStatsGrid>
        <StatCard>
          <StatValue>{stats.totalPending}</StatValue>
          <StatLabel>Total Pending</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.adminReviewPending}</StatValue>
          <StatLabel>Admin Review</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.appealPending}</StatValue>
          <StatLabel>Appeals</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.urgent}</StatValue>
          <StatLabel>Urgent</StatLabel>
        </StatCard>
      </QueueStatsGrid>

      {/* Filters */}
      <FiltersCard>
        <Select
          label="Status:"
          value={statusFilter}
          onChange={(value) => setStatusFilter(value)}
          options={[
            { value: 'all', label: 'All Status' },
            { value: 'ai_review_pending', label: 'AI Review Pending' },
            { value: 'admin_review_pending', label: 'Admin Review Pending' },
            { value: 'appeal_pending', label: 'Appeal Pending' },
            { value: 'auto_rejected', label: 'Auto Rejected' }
          ]}
        />

        <Select
          label="Urgency:"
          value={urgencyFilter}
          onChange={(value) => setUrgencyFilter(value)}
          options={[
            { value: 'all', label: 'All Urgency' },
            { value: 'high', label: 'High' },
            { value: 'medium', label: 'Medium' },
            { value: 'low', label: 'Low' }
          ]}
        />

        <Tooltip content="Approve multiple stories at once" position="top">
          <button className="btn btn-primary">
            Bulk Approve
          </button>
        </Tooltip>
      </FiltersCard>

      {/* Story Queue */}
      {loading ? (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          padding: '3rem',
          background: 'var(--color-white)',
          borderRadius: 'var(--border-radius-lg)',
          boxShadow: 'var(--shadow-md)'
        }}>
          <LoadingSpinner message="Loading stories..." />
        </div>
      ) : (
        <StoryQueue>
        {filteredStories.map(story => (
          <StoryCard key={story.id} $status={story.status}>
            <StoryHeader>
              <StoryTitle>
                {story.title}
                {story.urgency === 'high' && <span style={{ color: '#EF4444', marginLeft: '0.5rem' }}>🚨</span>}
              </StoryTitle>
              
              <StoryMeta>
                <MetaItem>
                  <span>👤</span>
                  {story.author}
                </MetaItem>
                <MetaItem>
                  <span>📖</span>
                  {story.genre}
                </MetaItem>
                <MetaItem>
                  <span>🕒</span>
                  {new Date(story.submittedAt).toLocaleString()}
                </MetaItem>
                <StatusBadge $status={story.status}>
                  {getStatusLabel(story.status)}
                </StatusBadge>
              </StoryMeta>

              <ActionButtons>
                <Tooltip content="View the complete story content" position="top">
                  <ActionButton
                    $variant="view"
                    onClick={() => console.log('View full story')}
                  >
                    📖 Read Full Story
                  </ActionButton>
                </Tooltip>
                {/* Show approve/reject/revision buttons for all stories - admin has final authority */}
                <>
                  <Tooltip content="Approve this story for public viewing" position="top">
                    <ActionButton
                      $variant="approve"
                      onClick={() => handleApproveClick(story.id, story.title)}
                      disabled={processingAction === story.id}
                    >
                      {processingAction === story.id ? '⏳' : '✅'} Approve
                    </ActionButton>
                  </Tooltip>
                  <Tooltip content="Request changes from the author" position="top">
                    <ActionButton
                      $variant="revision"
                      onClick={() => handleRequestRevisionClick(story.id, story.title)}
                      disabled={processingAction === story.id}
                    >
                      {processingAction === story.id ? '⏳' : '✏️'} Request Revision
                    </ActionButton>
                  </Tooltip>
                  <Tooltip content="Reject this story with a reason" position="top">
                    <ActionButton
                      $variant="reject"
                      onClick={() => handleRejectClick(story.id, story.title)}
                      disabled={processingAction === story.id}
                    >
                      {processingAction === story.id ? '⏳' : '❌'} Reject
                    </ActionButton>
                  </Tooltip>
                </>
                {story.urgency !== 'high' && (
                  <Tooltip content="Flag this story for immediate attention" position="top">
                    <ActionButton
                      $variant="priority"
                      onClick={() => handleMarkUrgentClick(story.id, story.title)}
                      disabled={processingAction === story.id}
                    >
                      {processingAction === story.id ? '⏳' : '🚨'} Mark Urgent
                    </ActionButton>
                  </Tooltip>
                )}
              </ActionButtons>
            </StoryHeader>

            <StoryContent>
              {story.aiAnalysis && (() => {
                const severity = calculateSeverity(
                  story.aiAnalysis!.confidence,
                  story.aiAnalysis!.violations.length
                );

                return (
                  <AIAnalysisCard>
                    <AIHeader>
                      <AITitle>
                        <span>🤖</span>
                        AI Content Analysis
                      </AITitle>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <SeverityBadge $severity={severity}>
                          {severity} Risk
                        </SeverityBadge>
                        <ConfidenceScore $score={story.aiAnalysis.confidence}>
                          {Math.round(story.aiAnalysis.confidence * 100)}% confidence
                        </ConfidenceScore>
                      </div>
                    </AIHeader>

                    <div style={{ marginBottom: '0.75rem' }}>
                      <strong style={{ fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                        Status:
                      </strong>
                      <span style={{
                        marginLeft: '0.5rem',
                        fontSize: '0.875rem',
                        color: story.aiAnalysis.appropriate ? '#059669' : '#DC2626',
                        fontWeight: 600
                      }}>
                        {story.aiAnalysis.appropriate ? '✓ Appropriate' : '✗ Inappropriate'}
                      </span>
                    </div>

                    {story.aiAnalysis.violations.length > 0 && (
                      <div style={{ marginBottom: '0.75rem' }}>
                        <strong style={{ fontSize: '0.875rem', color: 'var(--color-gray-700)', display: 'block', marginBottom: '0.5rem' }}>
                          Violations Detected:
                        </strong>
                        <ViolationsList>
                          {story.aiAnalysis.violations.map(violation => (
                            <ViolationTag key={violation}>
                              {formatViolations([violation])}
                            </ViolationTag>
                          ))}
                        </ViolationsList>
                      </div>
                    )}

                    {story.aiAnalysis.explanation && (
                      <div style={{ marginBottom: '0.75rem' }}>
                        <strong style={{ fontSize: '0.875rem', color: 'var(--color-gray-700)', display: 'block', marginBottom: '0.5rem' }}>
                          AI Explanation:
                        </strong>
                        <p style={{
                          margin: 0,
                          fontSize: '0.875rem',
                          color: 'var(--color-gray-600)',
                          fontStyle: 'italic',
                          lineHeight: '1.5'
                        }}>
                          "{story.aiAnalysis.explanation}"
                        </p>
                      </div>
                    )}

                    <AIMetadata>
                      {story.aiAnalysis.modelVersion && (
                        <span>
                          <strong>Model:</strong> {story.aiAnalysis.modelVersion}
                        </span>
                      )}
                      {story.aiAnalysis.analyzedAt && (
                        <span>
                          <strong>Analyzed:</strong> {new Date(story.aiAnalysis.analyzedAt).toLocaleString()}
                        </span>
                      )}
                    </AIMetadata>
                  </AIAnalysisCard>
                );
              })()}

              <div style={{ marginBottom: '1rem' }}>
                <strong style={{ color: 'var(--color-gray-700)' }}>Summary:</strong>
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: 'var(--color-gray-600)' }}>
                  {story.summary}
                </p>
              </div>

              <div>
                <strong style={{ color: 'var(--color-gray-700)' }}>Content Preview:</strong>
                <StoryPreview>
                  {story.content}
                </StoryPreview>
              </div>
            </StoryContent>
          </StoryCard>
        ))}
        
        {filteredStories.length === 0 && (
          <div style={{ 
            textAlign: 'center', 
            padding: '3rem', 
            color: 'var(--color-gray-500)',
            background: 'var(--color-white)',
            borderRadius: 'var(--border-radius-lg)',
            boxShadow: 'var(--shadow-md)'
          }}>
            🎉 No stories pending review! Great job keeping up with the queue.
          </div>
        )}
      </StoryQueue>
      )}

      {/* Modals */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        icon={confirmDialog.icon}
        onConfirm={confirmDialog.action}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />

      <RejectReasonModal
        isOpen={rejectModal.isOpen}
        storyTitle={rejectModal.storyTitle}
        onConfirm={handleRejectStory}
        onCancel={() => setRejectModal({ ...rejectModal, isOpen: false })}
      />

      <RequestRevisionModal
        isOpen={revisionModal.isOpen}
        storyTitle={revisionModal.storyTitle}
        onConfirm={handleRequestRevision}
        onCancel={() => setRevisionModal({ ...revisionModal, isOpen: false })}
      />

      <UrgentReasonModal
        isOpen={urgentModal.isOpen}
        storyTitle={urgentModal.storyTitle}
        onConfirm={handleMarkUrgent}
        onCancel={() => setUrgentModal({ ...urgentModal, isOpen: false })}
      />

      <SuccessAnimation
        isVisible={successAnimation.isVisible}
        variant={successAnimation.variant}
        message={successAnimation.message}
        onComplete={() => setSuccessAnimation({ ...successAnimation, isVisible: false })}
      />
    </StoryQueueContainer>
  );
};

export default StoryApprovalQueue;